/**
 * Your own account: the password, and the second factors.
 *
 * Separate from agency settings because it is the one screen here that is about *you* rather than
 * the business — an agent with no settings permission still has to be able to change their own
 * password and turn on an authenticator.
 */

import { useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useApiMutation } from "@/lib/mutations";
import { useSession } from "@/lib/session";
import { PasswordInput } from "@/components/password-input";
import { QrCode } from "@/components/qr-code";
import { Alert, Badge, Button, Card, CardTitle, Field, Input, PageHeader, Spinner } from "@/components/ui";

interface TwoFactorState {
  email: boolean;
  app: boolean;
  appPending: boolean;
  recoveryRemaining: number;
}

export function SecurityPage() {
  const { user } = useSession();
  const [mismatch, setMismatch] = useState(false);
  const [changed, setChanged] = useState(false);
  const [enrolment, setEnrolment] = useState<{ secret: string; uri: string } | null>(null);
  const [recovery, setRecovery] = useState<string[] | null>(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["two-factor"],
    queryFn: () => api.get<TwoFactorState>("/auth/two-factor"),
  });

  const changePassword = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.post("/auth/change-password", input),
    onSuccess: () => setChanged(true),
  });

  const toggleEmail = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.post("/auth/two-factor/email", input),
    invalidates: [["two-factor"]],
  });

  const startApp = useApiMutation({
    mutationFn: () => api.post<{ secret: string; uri: string }>("/auth/two-factor/app/start", {}),
    onSuccess: (result) => setEnrolment(result),
  });

  const confirmApp = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.post<{ recovery: string[] }>("/auth/two-factor/app/confirm", input),
    invalidates: [["two-factor"]],
    onSuccess: (result) => {
      setEnrolment(null);
      setRecovery(result.recovery);
    },
  });

  const disableApp = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.post("/auth/two-factor/app/disable", input),
    invalidates: [["two-factor"]],
    onSuccess: () => setRecovery(null),
  });

  const submitPassword = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const next = String(form.get("newPassword") ?? "");
    if (next !== String(form.get("confirm") ?? "")) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    setChanged(false);
    changePassword.mutate({ currentPassword: String(form.get("currentPassword") ?? ""), newPassword: next });
    event.currentTarget.reset();
  };

  if (isLoading || !data) return <Spinner />;

  return (
    <div className="max-w-2xl">
      <PageHeader
        title="Your security"
        back={{ to: "/app/settings", label: "Settings" }}
        subtitle={`Signed in as ${user?.email ?? ""}.`}
        description={"Your own account, not the agency's. Change your password here, and add a second step at sign-in — a code from an app, a code by email, or both."}
      />

      <div className="space-y-5">
        {/* ------------------------------------------------------------ password */}
        <Card>
          <CardTitle hint="You stay signed in here; other devices are not affected.">Password</CardTitle>

          <form onSubmit={submitPassword} className="space-y-4">
            {changePassword.errorMessage ? <Alert tone="danger">{changePassword.errorMessage}</Alert> : null}
            {mismatch ? <Alert tone="danger">The two new passwords are not the same.</Alert> : null}
            {changed ? <Alert tone="success">Your password has been changed.</Alert> : null}

            <Field label="Current password" htmlFor="currentPassword" required>
              <PasswordInput id="currentPassword" name="currentPassword" autoComplete="current-password" required />
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="New password" htmlFor="newPassword" required hint="At least 8 characters.">
                <PasswordInput id="newPassword" name="newPassword" autoComplete="new-password" required minLength={8} />
              </Field>
              <Field label="Type it again" htmlFor="confirm" required>
                <PasswordInput id="confirm" name="confirm" autoComplete="new-password" required minLength={8} />
              </Field>
            </div>

            <Button type="submit" disabled={changePassword.isPending}>
              {changePassword.isPending ? "Changing…" : "Change password"}
            </Button>
          </form>
        </Card>

        {/* -------------------------------------------------------- authenticator */}
        <Card>
          <CardTitle
            hint="A six-digit code from an app on your phone. The stronger of the two, and it works with no signal."
            action={data.app ? <Badge tone="success">On</Badge> : <Badge tone="muted">Off</Badge>}
          >
            Authenticator app
          </CardTitle>

          {recovery ? (
            <div className="space-y-3">
              <Alert tone="warn" title="Write these down now.">
                Ten single-use codes. They are the only way back in if you lose the phone, and this
                is the only time they are shown.
              </Alert>
              <div className="tabular grid grid-cols-2 gap-2 rounded-[var(--radius-surface)] bg-ink-50 p-4 text-sm sm:grid-cols-3">
                {recovery.map((code) => (
                  <span key={code}>{code}</span>
                ))}
              </div>
              <Button variant="secondary" onClick={() => setRecovery(null)}>
                I have saved them
              </Button>
            </div>
          ) : enrolment ? (
            <div className="space-y-4">
              <p className="text-sm text-ink-600">
                Scan this with your authenticator app, then type the six digits it shows.
              </p>

              {/*
                The camera first, because it is the way that cannot be got wrong. The alternative is
                thirty-two characters transcribed into a phone with no feedback on the way: one
                wrong character produces an app that shows six digits quite happily, every one of
                which is refused, and nothing on either screen says why.
              */}
              <div className="flex justify-center">
                <QrCode
                  value={enrolment.uri}
                  label="Scan to add this account to your authenticator app"
                />
              </div>

              <div className="rounded-[var(--radius-surface)] bg-ink-50 p-4">
                <p className="eyebrow">Or type this key in</p>
                {/* The key in text as well as a QR code, never instead of it: a phone cannot scan
                    the screen it is already being held against, desktop authenticators take typed
                    keys, and so does a password manager storing the factor alongside the password.
                    The code is the fast way, not the only way. */}
                <p className="tabular mt-1 break-all font-mono text-sm">{enrolment.secret}</p>
              </div>

              <form
                className="flex flex-wrap items-end gap-2"
                onSubmit={(event) => {
                  event.preventDefault();
                  const form = new FormData(event.currentTarget);
                  confirmApp.mutate({ code: String(form.get("code") ?? "") });
                }}
              >
                <Field label="The six digits" htmlFor="code" required>
                  <Input id="code" name="code" inputMode="numeric" required placeholder="123456" className="w-36" />
                </Field>
                <Button type="submit" disabled={confirmApp.isPending}>
                  {confirmApp.isPending ? "Checking…" : "Turn it on"}
                </Button>
                <Button variant="ghost" onClick={() => setEnrolment(null)}>
                  Cancel
                </Button>
              </form>

              {confirmApp.errorMessage ? <Alert tone="danger">{confirmApp.errorMessage}</Alert> : null}
            </div>
          ) : data.app ? (
            <form
              className="space-y-3"
              onSubmit={(event) => {
                event.preventDefault();
                const form = new FormData(event.currentTarget);
                disableApp.mutate({ password: String(form.get("password") ?? "") });
              }}
            >
              <p className="text-sm text-ink-600">
                On, with {data.recoveryRemaining} recovery {data.recoveryRemaining === 1 ? "code" : "codes"} left.
              </p>
              {disableApp.errorMessage ? <Alert tone="danger">{disableApp.errorMessage}</Alert> : null}
              <Field label="Your password, to turn it off" htmlFor="password" required>
                <PasswordInput id="password" autoComplete="current-password" required />
              </Field>
              <Button type="submit" variant="danger" disabled={disableApp.isPending}>
                Turn off the authenticator
              </Button>
            </form>
          ) : (
            <Button variant="secondary" onClick={() => startApp.mutate({})} disabled={startApp.isPending}>
              {startApp.isPending ? "Preparing…" : "Set up an authenticator app"}
            </Button>
          )}
        </Card>

        {/* -------------------------------------------------------------- by email */}
        <Card>
          <CardTitle
            hint="A code sent to your inbox each time you sign in. Weaker than an app — it protects a stolen password, not a stolen mailbox — but it needs nothing installed."
            action={data.email ? <Badge tone="success">On</Badge> : <Badge tone="muted">Off</Badge>}
          >
            Code by email
          </CardTitle>

          <Button
            variant={data.email ? "danger" : "secondary"}
            disabled={toggleEmail.isPending}
            onClick={() => toggleEmail.mutate({ enabled: !data.email })}
          >
            {data.email ? "Turn off email codes" : "Turn on email codes"}
          </Button>

          {data.email && data.app ? (
            <p className="mt-3 text-sm text-ink-500">
              Both are on. Either code will get you in — whichever you reach for first.
            </p>
          ) : null}
        </Card>
      </div>

      <button type="button" className="mt-4 text-sm text-ink-400 hover:text-ink-700" onClick={() => refetch()}>
        Refresh
      </button>
    </div>
  );
}
